/**
 * Feedback Modal Component
 *
 * Modal form for users to submit feedback (bugs, enhancements, feature requests).
 * Auto-collects context information (page URL, browser, etc.) to help with debugging.
 */

import { useState, useEffect } from 'react';
import { useFeedback } from '../../hooks/useFeedback';
import type { FeedbackType } from '../../types/feedback';
import { FEEDBACK_TYPE_LABELS } from '../../types/feedback';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FeedbackModal = ({ isOpen, onClose }: FeedbackModalProps) => {
  const { submitFeedback, isSubmitting } = useFeedback();
  const [type, setType] = useState<FeedbackType>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [charCount, setCharCount] = useState(0);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setType('bug');
      setTitle('');
      setDescription('');
      setCharCount(0);
    }
  }, [isOpen]);

  // Update character count
  useEffect(() => {
    setCharCount(description.length);
  }, [description]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (title.trim().length === 0) {
      return;
    }

    if (description.trim().length < 20) {
      return;
    }

    // Submit feedback
    const success = await submitFeedback({
      type,
      title: title.trim(),
      description: description.trim(),
    });

    if (success) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="flex items-center justify-center min-h-screen p-4">
        <div
          className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 animate-slide-in-right"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Send Feedback
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                What type of feedback? *
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {(Object.keys(FEEDBACK_TYPE_LABELS) as FeedbackType[]).map((feedbackType) => (
                  <button
                    key={feedbackType}
                    type="button"
                    onClick={() => setType(feedbackType)}
                    className={`
                      px-4 py-3 rounded-lg border-2 transition-all
                      ${type === feedbackType
                        ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                        : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-indigo-300 dark:hover:border-indigo-700'
                      }
                    `}
                  >
                    <div className="text-left">
                      <div className="font-medium text-sm">{FEEDBACK_TYPE_LABELS[feedbackType]}</div>
                      {feedbackType === 'bug' && <div className="text-xs mt-1 opacity-75">Something's broken</div>}
                      {feedbackType === 'enhancement' && <div className="text-xs mt-1 opacity-75">Make it better</div>}
                      {feedbackType === 'problem' && <div className="text-xs mt-1 opacity-75">General issue</div>}
                      {feedbackType === 'feature_request' && <div className="text-xs mt-1 opacity-75">New feature idea</div>}
                      {feedbackType === 'other' && <div className="text-xs mt-1 opacity-75">Something else</div>}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div className="mb-6">
              <label htmlFor="feedback-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Title *
              </label>
              <input
                id="feedback-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Brief summary of your feedback"
                maxLength={100}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                  focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                  placeholder-gray-400 dark:placeholder-gray-500"
                required
              />
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {title.length}/100 characters
              </div>
            </div>

            {/* Description */}
            <div className="mb-6">
              <label htmlFor="feedback-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description *
              </label>
              <textarea
                id="feedback-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Please describe in detail... Include steps to reproduce if it's a bug."
                rows={6}
                minLength={20}
                maxLength={2000}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                  focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                  placeholder-gray-400 dark:placeholder-gray-500 resize-none"
                required
              />
              <div className="flex justify-between text-xs mt-1">
                <span className={`
                  ${charCount < 20 ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}
                `}>
                  {charCount < 20 ? `Minimum 20 characters (${20 - charCount} more needed)` : 'Minimum met ✓'}
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  {charCount}/2000
                </span>
              </div>
            </div>

            {/* Info Note */}
            <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <p className="font-medium mb-1">Your feedback helps improve NIDOnote!</p>
                  <p className="text-xs opacity-90">
                    We'll automatically collect your browser info and the current page to help us investigate.
                    Your personal notes and events are never included.
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                  text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700
                  transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg
                  transition-colors flex items-center gap-2
                  disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting || title.trim().length === 0 || description.trim().length < 20}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Submit Feedback
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
